import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import {
  DashboardCompletionStepBox,
  ExpandablePanel,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import { Routes } from "../../root/routes";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { usePrivacyCenterSettings } from "../../components/superorg/settings/PrivacyCenterSettings";
import { BUSINESS_SURVEY_NAME } from "../../surveys/businessSurvey";
import { CONSUMER_INCENTIVES_SURVEY_NAME } from "../../surveys/consumerCollectionIncentivesSurvey";
import {
  EEA_UK_SURVEY_NAME,
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "../../surveys/eeaUkSurvey";
import { HR_SURVEY_NAME } from "../../surveys/hrSurvey";
import { allSurveyQuestionsWithVisibility } from "../surveys/BusinessSurveyAquiloPage";
import { PageLoading } from "../../components";
import { useEditPrivacyCenter } from "../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../hooks/usePrivacyCenterNotice";
import { useDataRetentionModule } from "../../components/organisms/data-retention/DataRetentionModule";
import { isDataRetentionComplete } from "./StayCompliantDataRetentionPage";
import { hasAnswered } from "../../surveys/survey";
import { isVendorDocumentationClassified } from "./vendor-documentation/VendorDocumentationPage";
import { VerticalProgressBarStepStatus } from "@truevault/aquilo/src/components/progressBar/VerticalProgressBarCirclesWithText";

export function DataMapDashboardPage() {
  const [org, ,] = usePrimaryOrganization();

  const [onboardingProgress] = useOnboardingProgress(org?.id);

  // Hooks for getting completion status of surveys

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings();

  const { answers: bizAnswers, flaggedForReviewSlugs: bizFlaggedForReviewSlugs } = useSurvey(
    org?.id,
    BUSINESS_SURVEY_NAME,
  );

  const { answers: incentivesAnswers, flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs } =
    useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME);

  const { answers: eeaAnswers, flaggedForReviewSlugs: eeaFlaggedForReviewSlugs } = useSurvey(
    org?.id,
    EEA_UK_SURVEY_NAME,
  );

  const { answers: hrAnswers, flaggedForReviewSlugs: hrFlaggedForReviewSlugs } = useSurvey(
    org?.id,
    HR_SURVEY_NAME,
  );

  const allFlaggedForReviewSlugs = useMemo(
    () => [
      ...bizFlaggedForReviewSlugs,
      ...incentivesFlaggedForReviewSlugs,
      ...hrFlaggedForReviewSlugs,
      ...eeaFlaggedForReviewSlugs,
    ],
    [
      bizFlaggedForReviewSlugs,
      incentivesFlaggedForReviewSlugs,
      hrFlaggedForReviewSlugs,
      eeaFlaggedForReviewSlugs,
    ],
  );

  const [isSurveyComplete, setIsSurveyComplete] = useState(null);
  useEffect(() => {
    if (
      isSurveyComplete === null &&
      org &&
      privacyCenter &&
      bizAnswers &&
      incentivesAnswers &&
      eeaAnswers &&
      hrAnswers &&
      pcSettings
    ) {
      let {
        bizQuestionsWithVisibility,
        incentivesQuestionsWithVisibility,
        hrQuestionsWithVisibility,
        eeaQuestionsWithVisibility,
      } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
        bizAnswers,
        hrAnswers,
        eeaAnswers,
        incentivesAnswers,
      });
      let bizSurveyVisibleQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
      let incentivesSurveyVisibleQuestions = incentivesQuestionsWithVisibility.filter(
        (q) => q.visible,
      );
      let hrVisibleQuestions = hrQuestionsWithVisibility.filter((q) => q.visible);
      let eeaVisibleQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
      const isDpoRequired =
        org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter);
      const hasDpo = Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
      const hasDesignatedRep = hasDesignatedRepresentative(pcSettings);
      let isBizSurveyComplete = bizSurveyVisibleQuestions
        .filter((q) => q.visible)
        .every((q) => hasAnswered(bizAnswers, q, /* debug = */ false));
      let isIncentivesSurveyComplete = incentivesSurveyVisibleQuestions
        .filter((q) => q.visible)
        .every((q) => hasAnswered(incentivesAnswers, q, /* debug = */ false));
      let isHrSurveyComplete = hrVisibleQuestions
        .filter((q) => q.visible)
        .every((q) => hasAnswered(hrAnswers, q, /* debug = */ false));
      let isEeaUkSurveyComplete =
        !org?.featureGdpr ||
        [...eeaVisibleQuestions].every(
          (
            q, // For the DPO question, it is considered answered if DPO is required and DPO is present, or if DPO is not required
          ) => {
            // DPO and Designated Rep completion is handled specially
            if (q.slug === HAS_DATA_PRIVACY_OFFICER_SLUG) {
              let dpoAnswer = eeaAnswers[q.slug];
              return isDpoRequired
                ? hasDpo || dpoAnswer === "false" || privacyCenter?.dpoOfficerDoesNotApply
                : true;
            }
            if (q.slug === HAS_DESIGNATED_REP_SLUG) {
              let designatedRepAnswer = eeaAnswers[q.slug];
              return (
                hasDesignatedRep ||
                designatedRepAnswer === "false" ||
                privacyCenter?.hasDesignatedRepTask ||
                privacyCenter?.eeaContactDoesNotApply
              );
            }
            return hasAnswered(eeaAnswers, q, /* debug = */ false);
          },
        );
      setIsSurveyComplete(
        isBizSurveyComplete &&
          isIncentivesSurveyComplete &&
          isHrSurveyComplete &&
          isEeaUkSurveyComplete,
      );
    }
  }, [
    org,
    privacyCenter,
    bizAnswers,
    incentivesAnswers,
    eeaAnswers,
    hrAnswers,
    pcSettings,
    isSurveyComplete,
    setIsSurveyComplete,
    allFlaggedForReviewSlugs,
  ]);

  // Hooks for getting completion status of Vendor Documentation

  const [allVendors, ,] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const [isVendorDocumentationFinished, setIsVendorDocumentationFinished] = useState(null);
  useEffect(() => {
    if (isVendorDocumentationFinished === null && allVendors && allVendors.length > 0) {
      setIsVendorDocumentationFinished(
        allVendors.every((v) => isVendorDocumentationClassified(org, v)),
      );
    }
  }, [allVendors, isVendorDocumentationFinished, setIsVendorDocumentationFinished]);

  // Hooks for getting completion status of data retention

  const editPrivacyCenter = useEditPrivacyCenter();

  const [draftNotice, ,] = usePrivacyCenterNotice(
    org?.id,
    privacyCenter?.id,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const dataRetentionModule = useDataRetentionModule();

  const [isRetentionFinished, setIsRetentionFinished] = useState(null);
  useEffect(() => {
    if (draftNotice && editPrivacyCenter && dataRetentionModule) {
      const isFinished = isDataRetentionComplete(
        draftNotice,
        editPrivacyCenter,
        dataRetentionModule,
      );
      setIsRetentionFinished(isFinished);
    }
  }, [
    draftNotice,
    editPrivacyCenter,
    dataRetentionModule,
    isRetentionFinished,
    setIsRetentionFinished,
  ]);

  if (
    isSurveyComplete === null ||
    isRetentionFinished === null ||
    isVendorDocumentationFinished === null ||
    !onboardingProgress
  ) {
    return <PageLoading />;
  }

  let dataRetentionItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-retention-icon.svg"
      title="Data Retention"
      description="Configure your data retention policy"
      status={isRetentionFinished ? "complete" : "finish-set-up"}
    />
  );

  let dataRetentionStepStatus: VerticalProgressBarStepStatus = isRetentionFinished
    ? "complete"
    : isSurveyComplete
    ? "current"
    : "upcoming";

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Data Map" marginBottomPx={24} />

      <div className="tw-boundary">
        <div className="mb-[48px]">
          <span className="font-bold text-neutral-700">Business Account:</span>
          &nbsp;
          <span>
            {org.name} · {privacyCenter.requestEmail}
          </span>
        </div>

        {(!isSurveyComplete || !isRetentionFinished) && (
          <ExpandablePanel title="Finish Data Map Setup" titleFontSize="2xl">
            <>
              <p className="font-normal text-[18px] text-neutral-800 mt-6">
                Complete the steps below to finish your Data Map:
              </p>
              <div className="mt-6">
                <VerticalProgressBarCirclesWithText
                  steps={[
                    {
                      name: "Business Profile",
                      description: "Answer questions about your business",
                      status: isSurveyComplete ? "complete" : "current",
                      route: Routes.organization.settings.businessSurvey,
                    },
                    {
                      name: "Data Map",
                      description: "Add your vendors and data disclosures",
                      status: "complete",
                      route: Routes.dataMap.root,
                    },
                    {
                      name: "Vendor Documents",
                      description: "Review your vendors classification and documentation",
                      status: isVendorDocumentationFinished
                        ? "complete"
                        : isSurveyComplete
                        ? "current"
                        : "upcoming",
                      route: Routes.dataMap.vendorDocumentation,
                    },
                    {
                      name: "Data Retention",
                      description: "Configure your data retention policy",
                      status: dataRetentionStepStatus,
                      route: Routes.dataMap.dataRetention,
                    },
                  ]}
                />
              </div>
            </>
          </ExpandablePanel>
        )}

        <div className="grid grid-cols-[1fr_1fr] gap-[16px] max-w-[830px] mt-[48px]">
          <a href={Routes.organization.settings.businessSurvey}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/business-survey-icon.svg"
              title="Business Profile"
              description="Answer questions about your business"
              status={isSurveyComplete ? "complete" : "finish-set-up"}
            />
          </a>
          <a href={Routes.dataMap.root}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-map-icon.svg"
              title="Data Map"
              description="Add your vendors and data disclosures"
              status={"complete"}
            />
          </a>
          <a href={Routes.dataMap.vendorDocumentation}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/vendor-documentation-icon.svg"
              title="Vendor Documents"
              description="Review your vendors classification and documentation"
              status={isVendorDocumentationFinished ? "complete" : "finish-set-up"}
            />
          </a>

          {dataRetentionStepStatus !== "upcoming" ? (
            <a href={Routes.dataMap.dataRetention}>{dataRetentionItem}</a>
          ) : (
            dataRetentionItem
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
}
